// Copyright (C) 2017-2023 Smart code 203358507

const React = require('react');
const { useParams, useLocation, useNavigate } = require('react-router');
const { useTranslation } = require('react-i18next');
const classnames = require('classnames');
const { useCore } = require('stremio/core');
const { useContentGamepadNavigation } = require('stremio/services/GamepadNavigation');
const { withCoreSuspender, useProfile } = require('stremio/common');
const { useNavigateWithOrigin } = require('stremio-router');
const { Button, HorizontalNavBar, DelayedRenderer, Image, MetaPreview } = require('stremio/components');
const StreamsList = require('./StreamsList');
const VideosList = require('./VideosList');
const { default: LiveTvDetails } = require('./LiveTvDetails');
const { default: LiveTvPlaceholder } = require('./LiveTvDetails/Placeholder');
const useMetaDetails = require('./useMetaDetails');
const useSeason = require('./useSeason');
const { default: useExternalPlayerCallback } = require('./useExternalPlayerCallback');
const useCarolineLibrary = require('../../caroline/library/useCarolineLibrary').default;
const membershipAction = require('../../caroline/library/membershipAction');
const resourceFailed = require('../../caroline/addons/resourceFailure');
const useSlowResource = require('../../caroline/addons/useSlowResource');
const findEpisode = require('../../caroline/catalogs/episodeIdentity');
const styles = require('./styles');

const GAMEPAD_HANDLER_ID = 'metadetails';

const MetaDetails = () => {
    const { type, id, videoId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { getStoredOrigin } = useNavigateWithOrigin();
    const contentRef = React.useRef(null);
    const videosScrollMemoryRef = React.useRef(null);
    const { t } = useTranslation();
    const core = useCore();
    const profile = useProfile();
    const carolineLibrary = useCarolineLibrary();
    const { add: addCarolineLibraryItem, remove: removeCarolineLibraryItem } = carolineLibrary;
    const urlParams = React.useMemo(() => ({
        type,
        id,
        videoId
    }), [type, id, videoId]);
    const metaDetails = useMetaDetails(urlParams);
    const metadataSlow = useSlowResource(metaDetails.metaItem?.content.type === 'Loading', JSON.stringify([type, id]));
    const readyMeta = metaDetails.metaItem?.content.type === 'Ready' ? metaDetails.metaItem.content.content : null;
    const metaAddonUrl = metaDetails.metaItem?.addon.transportUrl;
    const isEpgProvider = React.useMemo(() => {
        return profile.addons.some((addon) => addon.transportUrl === metaAddonUrl && addon.manifest.behaviorHints?.epgProvider === true);
    }, [profile.addons, metaAddonUrl]);
    const isLiveMeta = isEpgProvider && readyMeta !== null && (readyMeta.behaviorHints?.isLive === true || readyMeta.type === 'tv');
    const libraryMediaType = readyMeta?.type || type;
    const libraryMetaId = readyMeta?.id || id;
    const liveLibraryIdentityMatches = readyMeta !== null && readyMeta.type === type && readyMeta.id === id;
    const libraryMembership = isLiveMeta && !liveLibraryIdentityMatches ?
        undefined :
        carolineLibrary.isInLibrary(libraryMediaType, libraryMetaId);
    useExternalPlayerCallback(urlParams, metaDetails);
    const [season, setSeason] = useSeason(urlParams);
    const [metaPath, streamPath] = React.useMemo(() => {
        return metaDetails.selected !== null ?
            [metaDetails.selected.metaPath, metaDetails.selected.streamPath]
            :
            [null, null];
    }, [metaDetails.selected]);
    const video = React.useMemo(() => {
        return streamPath !== null && metaDetails.metaItem !== null && metaDetails.metaItem.content.type === 'Ready' ?
            metaDetails.metaItem.content.content.videos.reduce((result, video) => {
                if (video.id === streamPath.id) {
                    return video;
                }

                return result;
            }, null)
            :
            null;
    }, [metaDetails.metaItem, streamPath]);
    const externalPlayerCallbackCanMarkWatched = React.useMemo(() => {
        return typeof video?.id === 'string' &&
            metaDetails.libraryItem?.state?.video_id === video.id &&
            metaDetails.libraryItem?.state?.duration > 0;
    }, [metaDetails.libraryItem, video]);
    const addToLibrary = React.useCallback(() => {
        if (typeof libraryMediaType === 'string' && typeof libraryMetaId === 'string') {
            addCarolineLibraryItem({ mediaType: libraryMediaType, metaId: libraryMetaId }).catch(() => undefined);
        }
    }, [addCarolineLibraryItem, libraryMediaType, libraryMetaId]);
    const removeFromLibrary = React.useCallback(() => {
        if (typeof libraryMediaType === 'string' && typeof libraryMetaId === 'string') {
            removeCarolineLibraryItem({ mediaType: libraryMediaType, metaId: libraryMetaId }).catch(() => undefined);
        }
    }, [removeCarolineLibraryItem, libraryMediaType, libraryMetaId]);
    const toggleCarolineMembership = membershipAction(libraryMembership, addToLibrary, removeFromLibrary);
    const toggleWatched = React.useCallback(() => {
        if (metaDetails.metaItem === null || metaDetails.metaItem.content.type !== 'Ready') {
            return;
        }

        core.transport.dispatch({
            action: 'MetaDetails',
            args: {
                action: 'MarkAsWatched',
                args: !metaDetails.metaItem.content.content.watched
            }
        });
    }, [metaDetails, core.transport]);
    const toggleNotifications = React.useCallback(() => {
        if (metaDetails.libraryItem) {
            core.transport.dispatch({
                action: 'Ctx',
                args: {
                    action: 'ToggleLibraryItemNotifications',
                    args: [metaDetails.libraryItem._id, !metaDetails.libraryItem.state.noNotif],
                }
            });
        }
    }, [metaDetails.libraryItem, core.transport]);
    const seasonOnSelect = React.useCallback((event) => {
        setSeason(event.value);
    }, [setSeason]);
    const handleEpisodeSearch = React.useCallback((season, episode) => {
        const videos = metaDetails.metaItem?.content?.type === 'Ready' ? metaDetails.metaItem.content.content.videos : [];
        const selectedEpisode = findEpisode(videos, season, episode);
        if (!selectedEpisode) return;
        const searchVideoHash = encodeURIComponent(selectedEpisode.id);
        const url = location.pathname;
        const searchVideoPath = (urlParams.videoId === undefined || urlParams.videoId === null || urlParams.videoId === '') ?
            url + (!url.endsWith('/') ? '/' : '') + searchVideoHash
            : url.replace(/[^/]+\/?$/, searchVideoHash);
        navigate(searchVideoPath, { replace: true });
    }, [urlParams, location, navigate, metaDetails.metaItem]);
    const renderBackgroundImageFallback = React.useCallback(() => null, []);
    const renderBackground = React.useMemo(() => !!(
        metaPath &&
        metaDetails?.metaItem &&
        metaDetails.metaItem.content.type !== 'Loading' &&
        typeof metaDetails.metaItem.content.content?.background === 'string' &&
        metaDetails.metaItem.content.content.background.length > 0
    ), [metaPath, metaDetails]);
    const originPath = React.useMemo(() => getStoredOrigin(), [getStoredOrigin]);
    useContentGamepadNavigation(contentRef, GAMEPAD_HANDLER_ID);
    if (type === 'tv' && (metaDetails.selected === null || (isEpgProvider && metaDetails.metaItem?.content.type === 'Loading'))) {
        return (
            <div className={styles['metadetails-container']}>
                <HorizontalNavBar
                    className={styles['nav-bar']}
                    backButton={true}
                    fullscreenButton={true}
                    navMenu={true}
                    originPath={originPath}
                />
                <LiveTvPlaceholder />
            </div>
        );
    }
    if (isLiveMeta) {
        return (
            <LiveTvDetails
                key={readyMeta.id}
                className={styles['metadetails-container']}
                contentRef={contentRef}
                meta={readyMeta}
                streams={metaDetails.streams}
                libraryMembership={libraryMembership}
                onToggleLibrary={toggleCarolineMembership}
            >
                <HorizontalNavBar
                    className={styles['nav-bar']}
                    backButton={true}
                    fullscreenButton={true}
                    navMenu={true}
                    originPath={originPath}
                />
            </LiveTvDetails>
        );
    }
    return (
        <div className={styles['metadetails-container']}>
            {
                renderBackground ?
                    <div className={styles['background-image-layer']}>
                        <Image
                            className={styles['background-image']}
                            src={metaDetails.metaItem.content.content.background}
                            renderFallback={renderBackgroundImageFallback}
                            alt={''}
                        />
                    </div>
                    :
                    null
            }
            <HorizontalNavBar
                className={styles['nav-bar']}
                backButton={true}
                fullscreenButton={true}
                navMenu={true}
                originPath={originPath}
            />
            <div ref={contentRef} className={styles['metadetails-content']}>
                <div className={styles['details-primary-region']}>
                    {
                        metaPath === null ?
                            <DelayedRenderer delay={500}>
                                <div className={styles['meta-message-container']}>
                                    {typeof type === 'string' && typeof id === 'string' ?
                                        <MetaPreview.Placeholder className={styles['meta-preview']} /> :
                                        <div className={styles['message-label']}>{t('ERR_NO_META_SELECTED')}</div>}
                                </div>
                            </DelayedRenderer>
                            :
                            metaDetails.metaItem === null ?
                                <div className={styles['meta-message-container']}>
                                    <Image className={styles['image']} src={require('/assets/images/empty.png')} alt={''} />
                                    <div className={styles['message-label']}>{t('ERR_NO_META_FOUND')}</div>
                                </div>
                                :
                                metaDetails.metaItem.content.type === 'Err' ?
                                    <div className={styles['meta-message-container']}>
                                        <Image className={styles['image']} src={require('/assets/images/empty.png')} alt={''} />
                                        <div className={styles['message-label']}>{resourceFailed(metaDetails.metaItem)
                                            ? t('CAROLINE_METADATA_UNAVAILABLE', { defaultValue: 'Não foi possível carregar os detalhes. Volta a tentar mais tarde.' })
                                            : t('ERR_NO_META_FOUND')}</div>
                                        <Button className={styles['retry-button']} onClick={metaDetails.reload}>
                                            {t('CAROLINE_RETRY', { defaultValue: 'Tentar novamente' })}
                                        </Button>
                                    </div>
                                    :
                                    metaDetails.metaItem.content.type === 'Loading' ?
                                        metadataSlow ?
                                            <div className={styles['meta-message-container']} role={'status'}>
                                                <div className={styles['message-label']}>{t('CAROLINE_METADATA_SLOW', { defaultValue: 'Os detalhes estão a demorar mais do que o esperado. Continuamos a aguardar a fonte.' })}</div>
                                            </div>
                                            : <MetaPreview.Placeholder className={styles['meta-preview']} />
                                        :
                                        <React.Fragment>
                                            <MetaPreview
                                                className={classnames(styles['meta-preview'], 'animation-fade-in')}
                                                name={metaDetails.metaItem.content.content.name}
                                                logo={metaDetails.metaItem.content.content.logo}
                                                runtime={metaDetails.metaItem.content.content.runtime}
                                                releaseInfo={metaDetails.metaItem.content.content.releaseInfo}
                                                released={metaDetails.metaItem.content.content.released}
                                                description={
                                                    video !== null && typeof video.overview === 'string' && video.overview.length > 0 ?
                                                        video.overview
                                                        :
                                                        metaDetails.metaItem.content.content.description
                                                }
                                                links={metaDetails.metaItem.content.content.links}
                                                trailerStreams={metaDetails.metaItem.content.content.trailerStreams}
                                                mediaType={readyMeta?.type || type}
                                                inLibrary={libraryMembership === true}
                                                toggleInLibrary={toggleCarolineMembership}
                                                watched={metaDetails.metaItem.content.content.watched}
                                                toggleWatched={toggleWatched}
                                                metaId={metaDetails.metaItem.content.content.id}
                                                ratingInfo={metaDetails.ratingInfo}
                                            />
                                        </React.Fragment>
                    }
                </div>
                <div className={styles['details-secondary-region']}>
                    {
                        streamPath !== null ?
                            <StreamsList
                                className={styles['streams-list']}
                                streams={metaDetails.streams}
                                video={video}
                                streamId={streamPath.id}
                                metaId={metaPath?.id}
                                mediaName={readyMeta?.name}
                                mediaYear={readyMeta?.year || (typeof readyMeta?.releaseInfo === 'string' ? readyMeta.releaseInfo.slice(0, 4) : undefined)}
                                type={streamPath.type}
                                externalPlayerCallbackCanMarkWatched={externalPlayerCallbackCanMarkWatched}
                                onEpisodeSearch={handleEpisodeSearch}
                                onRetry={metaDetails.reload}
                            />
                            :
                            metaPath !== null ?
                                <VideosList
                                    className={styles['videos-list']}
                                    metaItem={metaDetails.metaItem}
                                    libraryItem={metaDetails.libraryItem}
                                    season={season}
                                    selectedVideoId={metaDetails.libraryItem?.state?.video_id}
                                    seasonOnSelect={seasonOnSelect}
                                    toggleNotifications={toggleNotifications}
                                    scrollMemoryRef={videosScrollMemoryRef}
                                    titleKey={JSON.stringify([metaPath.type, metaPath.id])}
                                />
                                :
                                null
                    }
                </div>
            </div>
        </div>
    );
};

const MetaDetailsFallback = () => {
    const { type } = useParams();
    return (
        <div className={styles['metadetails-container']}>
            <HorizontalNavBar
                className={styles['nav-bar']}
                backButton={true}
                fullscreenButton={true}
                navMenu={true}
            />
            {type === 'tv' && <LiveTvPlaceholder />}
        </div>
    );
};

module.exports = withCoreSuspender(MetaDetails, MetaDetailsFallback);
